import {
  PropsWithChildren,
  ReactNode,
  useState,
  useMemo,
  SetStateAction,
  Dispatch,
  useEffect,
  useRef,
  FC,
} from 'react';
import formatDate from 'date-fns/format';

import createSafeContext from '@paradigm/utils/src/createSafeContext';
import { Nullable } from '@paradigm/utils/types';
import {
  razzmatazz,
  paradigmLightBlue,
  neonLemon,
  grapefruit,
} from '@paradigm/design-system/src/colors';

import {
  ParsedPlatformState,
  ParsedPlatformStateEnum,
  PlatformState,
  PlatformStateEnum,
  parsedPlatformStateValues,
  platformStateValues,
} from '@paradigm/api/types';

import {
  FetcherState,
  FetcherStatus,
} from '#/positions/contexts/abstractions/useFetcher';
import {
  SubscriptionState,
  SubscriptionStatus,
} from '#/positions/contexts/abstractions/useSubscriber';
import { usePlatformStateFetcherState } from './fetcher';
import { usePlatformStateSubscriberStatus } from './subscriber';

export interface PlatformStateState {
  readonly data: PlatformState;
}

export interface PlatformStateActions {
  readonly setState: Dispatch<SetStateAction<PlatformStateState>>;
  readonly reset: () => void;
}

export interface PlatformStateToastNotification {
  readonly hidePlatformState: boolean;
  readonly shouldNotify: boolean;
  readonly color: string;
  readonly closable: boolean;
}

export interface PlatformStateView {
  readonly data: ParsedPlatformState;
  readonly notification: PlatformStateToastNotification;
  readonly status: FetcherStatus;
  readonly error: Nullable<string>;
}

const INITIAL_STATE: { data: PlatformState } = {
  data: {
    messageKind: '',
    platformState: platformStateValues.AVAILABLE,
    nextPlatformState: platformStateValues.UNAVAILABLE,
    nextPlatformStateAt: undefined,
    publishedAt: undefined,
  },
};

function createView(
  state: PlatformStateState,
  fetcherState: FetcherState,
  subscriberState: SubscriptionState,
  syncDateTime?: number,
  previousState?: PlatformStateEnum,
): PlatformStateView {
  const statusView = createViewStatus(
    fetcherState.status,
    subscriberState.status,
  );
  const { data } = state;
  const {
    messageKind,
    nextPlatformState,
    platformState,
    nextPlatformStateAt,
    publishedAt,
  } = data;
  const synced = statusView === 'SYNCED';

  // Note: The following block of code attempts to capture a state of `undefined` as AVAILABLE
  const wasUnavailable = previousState === platformStateValues.UNAVAILABLE;
  const wasAvailableCancelOnly =
    previousState === platformStateValues.AVAILABLE_CANCEL_ONLY;
  const wasAvailable = !wasUnavailable && !wasAvailableCancelOnly;
  const isUnavailable = platformState === platformStateValues.UNAVAILABLE;
  const isAvailableCancelOnly =
    platformState === platformStateValues.AVAILABLE_CANCEL_ONLY;
  const isAvailable = !isUnavailable && !isAvailableCancelOnly;

  // Note: This is how we only isolate AVAILABLE notifications to those recieved after App initialization.
  const isNewerNotification =
    publishedAt !== undefined &&
    syncDateTime !== undefined &&
    syncDateTime < publishedAt;

  const updateKeepAvailable = synced && wasAvailable && isAvailable;
  const shouldNotify = isNewerNotification || !isAvailable;

  const stateColors = {
    [platformStateValues.AVAILABLE]: neonLemon,
    [platformStateValues.UNAVAILABLE]: razzmatazz,
    [platformStateValues.AVAILABLE_CANCEL_ONLY]: grapefruit,
    DEFAULT: paradigmLightBlue,
  };

  const color = !updateKeepAvailable
    ? stateColors[platformState]
    : stateColors.DEFAULT;

  const parseMessage = (
    rawState: PlatformStateEnum,
  ): ParsedPlatformStateEnum => {
    const stateMessages: Record<PlatformStateEnum, ParsedPlatformStateEnum> = {
      UNAVAILABLE: parsedPlatformStateValues.UNAVAILABLE,
      AVAILABLE: parsedPlatformStateValues.AVAILABLE,
      AVAILABLE_CANCEL_ONLY: parsedPlatformStateValues.AVAILABLE_CANCEL_ONLY,
    };
    return stateMessages[rawState];
  };

  const notification: PlatformStateToastNotification = {
    color,
    closable: isAvailable, // Note: If platform is AVAILABLE, toast should be closable.
    shouldNotify,
    hidePlatformState: updateKeepAvailable,
  };

  return {
    data: {
      messageKind,
      nextPlatformState: parseMessage(nextPlatformState),
      platformState: parseMessage(platformState),
      nextPlatformStateAt:
        nextPlatformStateAt !== undefined
          ? formatPlatformAtDate(nextPlatformStateAt)
          : undefined,
      publishedAt:
        publishedAt !== undefined
          ? formatPlatformAtDate(publishedAt)
          : undefined,
    },
    status: statusView,
    error: subscriberState.error || fetcherState.error,
    notification,
  };
}

function createViewStatus(
  fetcherStatus: FetcherStatus,
  subscriberStatus: SubscriptionStatus,
) {
  return subscriberStatus === 'SUBSCRIBED'
    ? fetcherStatus
    : subscriberStatus === 'ERROR'
    ? 'ERROR'
    : 'LOADING';
}

function createActions(
  setState: Dispatch<SetStateAction<PlatformStateState>>,
): PlatformStateActions {
  return {
    setState,
    reset() {
      setState(INITIAL_STATE);
    },
  };
}

const formatPlatformAtDate = (date: number): string =>
  formatDate(date / 1000000, 'HH:mm dd MMM yy');

function createPlatformStateStoreProvider() {
  const [usePlatformState, PlatformStateProvider] =
    createSafeContext<PlatformStateState>('PlatformStateState');

  const [usePlatformStateActions, PlatformStateActionsProvider] =
    createSafeContext<PlatformStateActions>('PlatformStateActions');

  const PlatformStateStoreProvider = (props: { children: ReactNode }) => {
    const { children } = props;
    const [state, setState] = useState<PlatformStateState>(INITIAL_STATE);
    const actions = useMemo(() => createActions(setState), []);

    return (
      <PlatformStateActionsProvider value={actions}>
        <PlatformStateProvider value={state}>{children}</PlatformStateProvider>
      </PlatformStateActionsProvider>
    );
  };

  return {
    PlatformStateStoreProvider,
    usePlatformState,
    usePlatformStateActions,
  };
}

function createPlatformStateViewProvider() {
  const [usePlatformStateView, PlatformStateViewContext] =
    createSafeContext<PlatformStateView>('PlatformStateView');

  const PlatformStateViewProvider: FC<
    PropsWithChildren<{
      usePlatformState: () => PlatformStateState;
    }>
  > = (props) => {
    const { children, usePlatformState } = props;
    const fetcherState = usePlatformStateFetcherState();
    const subscriberState = usePlatformStateSubscriberStatus();
    const state = usePlatformState();

    // Note: This pattern of useRef is discouraged. This is a derivative of
    // usePrevious pattern. Which itself is discouraged by React. This case
    // of storing previous can be achieved through a useState.
    //
    // https://legacy.reactjs.org/docs/hooks-faq.html#how-to-get-the-previous-props-or-state
    const syncDateTime = useRef<number | undefined>(state.data.publishedAt);
    const previousState = useRef<PlatformStateEnum | undefined>(
      state.data.platformState,
    );

    // Note 2: Calculate view from previous ref.current happens before the following
    // useEffect block. This is important because the previous ref value is used
    const view = useMemo(
      () =>
        createView(
          state,
          fetcherState,
          subscriberState,
          syncDateTime.current,
          previousState.current,
        ),
      [state, fetcherState, subscriberState],
    );

    // Note 3: Set the next previous ref.current value.
    useEffect(() => {
      syncDateTime.current = state.data.publishedAt;
      previousState.current = state.data.platformState;
    }, [state]);

    return (
      <PlatformStateViewContext value={view}>
        {children}
      </PlatformStateViewContext>
    );
  };

  return { PlatformStateViewProvider, usePlatformStateView };
}

//NOTE - Future Spread Contexts
export const {
  PlatformStateStoreProvider: FSPlatformStateStoreProvider,
  usePlatformState: useFsPlatformState,
  usePlatformStateActions: useFsPlatformStateActions,
} = createPlatformStateStoreProvider();

export const {
  PlatformStateViewProvider: FSPlatformStateViewProvider,
  usePlatformStateView: useFsPlatformStateView,
} = createPlatformStateViewProvider();

//NOTE - Unified Market  (GRFQ) Contexts
export const {
  PlatformStateStoreProvider: GrfqPlatformStateStoreProvider,
  usePlatformState: useGrfqPlatformState,
  usePlatformStateActions: useGrfqPlatformStateActions,
} = createPlatformStateStoreProvider();

export const {
  PlatformStateViewProvider: GrfqPlatformStateViewProvider,
  usePlatformStateView: useGrfqPlatformStateView,
} = createPlatformStateViewProvider();

//NOTE - Unified Market  (DRFQ) Contexts
export const {
  PlatformStateStoreProvider: DrfqPlatformStateStoreProvider,
  usePlatformState: useDrfqPlatformState,
  usePlatformStateActions: useDrfqPlatformStateActions,
} = createPlatformStateStoreProvider();

export const {
  PlatformStateViewProvider: DrfqPlatformStateViewProvider,
  usePlatformStateView: useDrfqPlatformStateView,
} = createPlatformStateViewProvider();
